import { useEffect, useRef, useState, type FormEvent } from "react";
import { api, ApiError } from "../../api/client";
import { usePermission } from "../../hooks/usePermission";
import { Modal } from "../../components/ui/Modal";
import type { Member } from "../../types";

interface Props {
  member: Member | null; // null = neues Mitglied
  ranks: string[];
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Anlegen/Bearbeiten eines Mitglieds. Beim Bearbeiten werden Felder, für
 * die der aktuelle Benutzer keine Berechtigung besitzt, deaktiviert UND
 * beim Speichern gar nicht erst mitgesendet - so löst ein unveränderter,
 * schreibgeschützter Feldwert niemals versehentlich einen 403-Fehler aus.
 *
 * Der Rang ist ein rein freies, manuell gepflegtes Feld - auch bei mit einem
 * Benutzerkonto verknüpften Mitgliedern (member.user_id gesetzt, siehe
 * backend/src/utils/memberSync.ts) jederzeit über "members.change_rank"
 * änderbar. Keine automatische Synchronisierung mit der Benutzerrolle mehr
 * (Änderung September 2026).
 */
export function MemberForm({ member, ranks, onClose, onSaved }: Props) {
  const isEdit = !!member;
  const hasEditPermission = usePermission("members.edit");
  const hasEditPhonePermission = usePermission("members.edit_phone");
  const hasChangeRankPermission = usePermission("members.change_rank");
  const hasEditNotesPermission = usePermission("members.edit_notes");
  const isLinked = isEdit && member!.user_id !== null;
  const canEditName = !isEdit || hasEditPermission;
  const canEditPhone = !isEdit || hasEditPhonePermission;
  const canChangeRank = !isEdit || hasChangeRankPermission;
  const canEditNotes = !isEdit || hasEditNotesPermission;

  const [firstName, setFirstName] = useState(member?.first_name ?? "");
  const [lastName, setLastName] = useState(member?.last_name ?? "");
  const [phone, setPhone] = useState(member?.phone ?? "");
  const [rank, setRank] = useState(member?.rank ?? "");
  const [note, setNote] = useState(member?.note ?? "");
  const [excludeFromCount, setExcludeFromCount] = useState(!!member?.exclude_from_count);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const datalistId = useRef(`ranks-${Math.random().toString(36).slice(2)}`).current;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (isEdit) {
        const payload: Record<string, unknown> = {};
        if (canEditName) { payload.firstName = firstName; payload.lastName = lastName; }
        if (canEditPhone) payload.phone = phone;
        if (canChangeRank) payload.rank = rank;
        if (canEditNotes) payload.note = note;
        if (canEditName) payload.excludeFromCount = excludeFromCount;
        await api.put(`/api/members/${member!.id}`, payload);
      } else {
        await api.post("/api/members", { firstName, lastName, phone, rank, note });
      }
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={isEdit ? "Mitglied bearbeiten" : "Neues Mitglied"}
      onClose={onClose}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>Abbrechen</button>
          <button className="btn-primary" form="member-form" disabled={submitting}>{submitting ? "Speichern…" : "Speichern"}</button>
        </>
      }
    >
      <form id="member-form" onSubmit={handleSubmit} className="space-y-4">
        {error && <div className="bg-danger-muted text-danger text-sm rounded-lg px-3 py-2">{error}</div>}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Vorname *</label>
            <input className="input" value={firstName} onChange={(e) => setFirstName(e.target.value)} disabled={!canEditName} required />
          </div>
          <div>
            <label className="label">Nachname *</label>
            <input className="input" value={lastName} onChange={(e) => setLastName(e.target.value)} disabled={!canEditName} required />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Telefonnummer</label>
            <input className="input" value={phone ?? ""} onChange={(e) => setPhone(e.target.value)} disabled={!canEditPhone} placeholder="+49 …" />
          </div>
          <div>
            <label className="label">Rang</label>
            <input className="input" list={datalistId} value={rank ?? ""} onChange={(e) => setRank(e.target.value)} disabled={!canChangeRank} placeholder="z.B. Prospect, Officer, President" />
            <datalist id={datalistId}>
              {ranks.map((r) => <option key={r} value={r} />)}
            </datalist>
            {isLinked && (
              <p className="text-xs text-gray-500 mt-1">
                🔗 Verknüpft mit Benutzerkonto "{member!.linked_username}".
              </p>
            )}
          </div>
        </div>

        <div>
          <label className="label">Notiz</label>
          <textarea className="input" rows={3} value={note ?? ""} onChange={(e) => setNote(e.target.value)} disabled={!canEditNotes} />
        </div>

        {isEdit && (
          <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={excludeFromCount}
              onChange={(e) => setExcludeFromCount(e.target.checked)}
              disabled={!canEditName}
            />
            Nicht als aktives Mitglied zählen (bleibt überall sichtbar, zählt aber nicht bei „Aktuelle Mitglieder")
          </label>
        )}

        {isEdit && (!canEditName || !canEditPhone || !canChangeRank || !canEditNotes) && (
          <p className="text-xs text-gray-500">
            Ausgegraute Felder sind aufgrund fehlender Berechtigung schreibgeschützt.
          </p>
        )}
      </form>
    </Modal>
  );
}
